import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  Activity,
  StickyNote,
  Loader2,
  FileText,
  FilePlus,
  UserPlus,
  UserMinus,
  RefreshCw,
  Sparkles,
  Gavel,
  Phone,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { CaseEventTypeLabel, type CaseEventDto, type CaseEventType } from '@toga/shared'

interface Props {
  caseId: string
  canWrite: boolean
}

function iconFor(type: CaseEventType) {
  switch (type) {
    case 'CASE_CREATED':
      return <Sparkles size={14} className="text-toga-300" />
    case 'STATUS_CHANGED':
      return <RefreshCw size={14} className="text-amber-300" />
    case 'ASSIGNMENT_ADDED':
      return <UserPlus size={14} className="text-emerald-300" />
    case 'ASSIGNMENT_REMOVED':
      return <UserMinus size={14} className="text-rose-300" />
    case 'DOCUMENT_ADDED':
      return <FilePlus size={14} className="text-blue-300" />
    case 'DOCUMENT_VERSION_ADDED':
      return <FileText size={14} className="text-blue-300" />
    case 'NOTE':
      return <StickyNote size={14} className="text-fuchsia-300" />
    case 'COURT_NOTIFICATION':
      return <Gavel size={14} className="text-orange-300" />
    case 'CONTACT_LOG':
      return <Phone size={14} className="text-sky-300" />
  }
}

export default function TimelinePanel({ caseId, canWrite }: Props) {
  const [events, setEvents] = useState<CaseEventDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const reload = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: CaseEventDto[] }>(`/cases/${caseId}/events`)
      setEvents(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar eventos')
    }
  }, [caseId])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Activity size={16} className="text-toga-300" />
          Cronología
          {events && <span className="text-xs t-muted font-normal">({events.length})</span>}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <StickyNote size={14} /> {showForm ? 'Cancelar' : 'Añadir nota'}
          </button>
        )}
      </div>

      {showForm && (
        <NoteForm
          caseId={caseId}
          onCreated={() => {
            setShowForm(false)
            void reload()
          }}
        />
      )}

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 mb-3">
          {error}
        </div>
      )}

      {!events ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : events.length === 0 ? (
        <p className="t-muted text-sm">Sin eventos todavía.</p>
      ) : (
        <ol className="relative space-y-3 pl-5 mt-2">
          <span
            className="absolute left-2 top-1 bottom-1 w-px"
            style={{ background: 'var(--border)' }}
          />
          {events.map((e) => (
            <li key={e.id} className="relative">
              <span
                className="absolute -left-[15px] top-1 w-5 h-5 rounded-full grid place-items-center"
                style={{
                  background: 'var(--bg-surface-strong)',
                  border: '1px solid var(--border)',
                }}
              >
                {iconFor(e.type)}
              </span>
              <div className="text-sm t-primary leading-snug">{e.title}</div>
              {e.description && <div className="text-xs t-secondary mt-1">{e.description}</div>}
              <div className="text-xs t-faint mt-1">
                {new Date(e.occurredAt).toLocaleString('es-ES')}
                {e.createdByName && ` · ${e.createdByName}`}
                <span className="ml-2 text-[10px] uppercase tracking-wider text-toga-300/80">
                  {CaseEventTypeLabel[e.type]}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function NoteForm({ caseId, onCreated }: { caseId: string; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<'NOTE' | 'COURT_NOTIFICATION' | 'CONTACT_LOG'>('NOTE')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.post(`/cases/${caseId}/events`, {
        title,
        description: description || null,
        type,
      })
      setTitle('')
      setDescription('')
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError('Error de red')
      }
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <form onSubmit={onSubmit} className="space-y-2 surface-soft p-3 mb-3">
      <div className="grid grid-cols-3 gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
          className={`${inputCls} col-span-1`}
        >
          <option value="NOTE">Nota</option>
          <option value="COURT_NOTIFICATION">Notificación juzgado</option>
          <option value="CONTACT_LOG">Comunicación</option>
        </select>
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título corto"
          className={`${inputCls} col-span-2`}
        />
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Detalle (opcional)"
        className={inputCls}
      />
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="text-right">
        <button
          type="submit"
          disabled={busy || !title}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 inline-flex items-center gap-1"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          Añadir
        </button>
      </div>
    </form>
  )
}
